import { useState, useRef, useCallback, useEffect } from 'react';
import qs from 'qs';
import equal from 'deep-equal';
import { onLogout, makeRequest, useAppDispatch } from '@wappsto/redux';
import useMounted from './useMounted';
import { STATUS } from '../util';

interface LogsCacheEntry {
  data: any[];
  options: any;
  status: string;
}

let cache: Record<string, LogsCacheEntry> = {};
onLogout(() => {
  cache = {};
});

export default function useLogs(stateId: string, sessionId: string, cacheId?: string) {
  const [data, setData] = useState<any[]>([]);
  const cachedData = useRef<any[]>([]);
  const cachedStatus = useRef<string>(STATUS.IDLE);
  const isCanceled = useRef(false);
  const [status, setStatus] = useState<string>(STATUS.IDLE);
  const dispatch = useAppDispatch();
  const cancelFunc = useRef<((reason?: any) => void) | null>(null);
  const isMounted = useMounted();

  const setCurrentStatus = (currentStatus: string) => {
    cachedStatus.current = currentStatus;
    setStatus(currentStatus);
  };

  useEffect(() => {
    cache = {};
  }, []);

  const getLogs = useCallback(
    async (options?: any) => {
      if (!stateId) {
        throw new Error('stateId is not defined');
      }
      const cOptions: any = { ...options };
      if (!cOptions.start || !cOptions.end) {
        return;
      }
      if (cacheId && cache[cacheId] && equal(cache[cacheId].options, options)) {
        setData(cache[cacheId].data);
        setCurrentStatus(cache[cacheId].status);
        return;
      }
      if (cOptions.start.constructor === Date) {
        cOptions.start = cOptions.start.toISOString();
      }
      if (cOptions.end.constructor === Date) {
        cOptions.end = cOptions.end.toISOString();
      }
      if (cachedStatus.current === STATUS.IDLE) {
        setData([]);
        setCurrentStatus(STATUS.PENDING);
        isCanceled.current = false;
        cachedData.current = [];
        let more = true;
        try {
          while (more && !isCanceled.current && (cacheId || (!cacheId && isMounted.current))) {
            if (cachedData.current.length > 0) {
              const last = cachedData.current[cachedData.current.length - 1];
              cOptions.start = last.time || last.selected_timestamp;
            }
            const url = `/log/${stateId}?type=state${
              cOptions.limit ? '' : '&limit=3600'
            }&${qs.stringify(cOptions)}`;
            const controller = new AbortController();
            cancelFunc.current = controller.abort.bind(controller);
            const result: any = await dispatch(
              makeRequest({
                url,
                method: 'GET',
                signal: controller.signal,
                dispatchEntities: false,
                headers: {
                  'x-session': sessionId,
                },
              }) as any,
            );

            cancelFunc.current = null;
            if (isCanceled.current) {
              return;
            }
            if (!result.ok || !result.json) {
              throw new Error('error');
            }
            cachedData.current = cachedData.current.concat(result.json.data);
            more = result.json.more && (!cOptions.limit || result.json.length < cOptions.limit);
          }
          if (cacheId) {
            cache[cacheId] = {
              data: cachedData.current,
              options,
              status: STATUS.SUCCESS,
            };
          }
          if (!isMounted.current) {
            return;
          }
          if (!isCanceled.current) {
            setData(cachedData.current);
          }
          setCurrentStatus(STATUS.SUCCESS);
        } catch (e) {
          cancelFunc.current = null;
          if (cacheId) {
            cache[cacheId] = {
              data: cachedData.current,
              options,
              status: STATUS.ERROR,
            };
          }
          if (!isMounted.current) {
            return;
          }
          if (!isCanceled.current) {
            setData(cachedData.current);
          }
          setCurrentStatus(STATUS.ERROR);
        }
      }
    },
    [cacheId, dispatch, sessionId, stateId, isMounted],
  );

  const cancel = useCallback(() => {
    if (cancelFunc.current) {
      cancelFunc.current('Operation canceled');
    }
    if (!isCanceled.current) {
      setCurrentStatus(STATUS.CANCELED);
      isCanceled.current = true;
    }
  }, []);

  const reset = useCallback(
    (resetCache = true) => {
      if (cancelFunc.current) {
        cancelFunc.current('Operation canceled');
      }
      if (cacheId && resetCache) {
        delete cache[cacheId];
      }
      setData([]);
      setCurrentStatus(STATUS.IDLE);
      isCanceled.current = false;
      cachedData.current = [];
    },
    [cacheId],
  );

  return { data, status, getLogs, reset, cancel };
}
